import { usePwa } from "@/context/PwaContext";
import { MoreVertical, Smartphone, X, Copy, Compass, Check } from "lucide-react";
import { toast } from "sonner";

// Accurate iOS "Share" glyph (rounded square with an up arrow) so older users recognise it.
function IOSShareIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M12 3v11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8 6l4-4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7 10H5.5A1.5 1.5 0 0 0 4 11.5V19a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7.5A1.5 1.5 0 0 0 18.5 10H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function PwaInstall() {
  const pwa = usePwa();
  if (!pwa || !pwa.sheet) return null;
  const { sheet, requestInstall, dismiss, close } = pwa;

  const copyLink = async () => {
    try { await navigator.clipboard.writeText("https://ineedaproverb.com"); toast.success("Link copied — now open Safari and paste it."); }
    catch { toast.message("Open Safari and go to ineedaproverb.com"); }
  };

  const Overlay = ({ children, onClose, testid }) => (
    <div data-testid={testid} className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>{children}</div>
    </div>
  );

  if (sheet === "prompt") {
    return (
      <Overlay testid="pwa-prompt" onClose={dismiss}>
        <div className="flex items-start gap-4">
          <div className="shrink-0 w-12 h-12 rounded-2xl bg-[#EAF1EA] flex items-center justify-center">
            <Smartphone className="w-6 h-6 text-[#2C4C3B]" strokeWidth={1.8} />
          </div>
          <div className="flex-1">
            <h3 className="font-serif-display text-2xl text-[#0F172A] leading-snug">Want quicker access next time?</h3>
            <p className="mt-2 text-base text-[#475569]">Save I Need a Proverb to your Home Screen.</p>
            <p className="mt-2 text-sm text-[#64748B]">On iPhone: open this site in Safari, tap Share, then tap Add to Home Screen.</p>
          </div>
        </div>
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <button data-testid="pwa-save-btn" onClick={requestInstall}
            className="order-1 sm:order-2 flex-1 rounded-full bg-[#2C4C3B] text-white py-3.5 text-base font-semibold hover:bg-[#1E3629] transition-colors">
            Save to Phone
          </button>
          <button data-testid="pwa-notnow-btn" onClick={dismiss}
            className="order-2 sm:order-1 flex-1 rounded-full border border-[#E2E8F0] text-[#475569] py-3.5 text-base font-semibold hover:bg-[#F4F1EA] transition-colors">
            Not Now
          </button>
        </div>
      </Overlay>
    );
  }

  if (sheet === "installed") {
    return (
      <Overlay testid="pwa-installed" onClose={close}>
        <div className="text-center py-2">
          <div className="mx-auto w-14 h-14 rounded-full bg-[#EAF1EA] flex items-center justify-center">
            <Check className="w-8 h-8 text-[#2C4C3B]" strokeWidth={2.2} />
          </div>
          <h3 className="mt-4 font-serif-display text-2xl text-[#0F172A]">You're all set!</h3>
          <p className="mt-2 text-base text-[#475569]">Find the <strong>I Need a Proverb</strong> icon on your Home Screen and tap it any time you need a little wisdom.</p>
          <button data-testid="pwa-installed-done" onClick={close} className="mt-6 w-full rounded-full bg-[#2C4C3B] text-white py-3.5 text-base font-semibold hover:bg-[#1E3629] transition-colors">Done</button>
        </div>
      </Overlay>
    );
  }

  if (sheet === "ios-open-safari") {
    return (
      <Overlay testid="pwa-ios-safari" onClose={close}>
        <Header title="First, open this page in Safari" onClose={close} />
        <p className="mt-2 text-base text-[#475569]">You're viewing inside another app's browser. The "Add to Home Screen" button only shows in Apple's <strong>Safari</strong>.</p>
        <ol className="mt-4 space-y-4">
          <Step n="1">Tap the menu icon <MoreVertical className="inline w-5 h-5 text-[#2C4C3B] align-text-bottom" strokeWidth={1.8} /> (usually top‑right corner).</Step>
          <Step n="2">Tap <strong>&ldquo;Open in Safari&rdquo;</strong> <Compass className="inline w-5 h-5 text-[#2C4C3B] align-text-bottom" strokeWidth={1.8} />.</Step>
          <Step n="3">In Safari, tap <strong>Save to Phone</strong> again — then follow the 3 quick steps.</Step>
        </ol>
        <button data-testid="pwa-copy-link" onClick={copyLink} className="mt-5 w-full rounded-full border border-[#E2E8F0] text-[#2C4C3B] py-3 text-base font-semibold hover:bg-[#F4F1EA] transition-colors inline-flex items-center justify-center gap-2">
          <Copy className="w-4 h-4" /> Copy link for Safari
        </button>
        <button data-testid="pwa-ios-safari-done" onClick={close} className="mt-3 w-full rounded-full bg-[#2C4C3B] text-white py-3.5 text-base font-semibold hover:bg-[#1E3629] transition-colors">Got it</button>
      </Overlay>
    );
  }

  if (sheet === "ios") {
    return (
      <Overlay testid="pwa-ios" onClose={close}>
        <Header title="Save I Need a Proverb to Your Home Screen" onClose={close} />
        <p className="mt-2 text-base text-[#475569]">For quick access anytime:</p>
        <ol className="mt-4 space-y-4">
          <Step n="1">Open I Need a Proverb in <strong>Safari</strong>.</Step>
          <Step n="2">Tap the <strong>Share</strong> button — the square with the upward arrow <IOSShareIcon className="inline-block w-5 h-5 text-[#2C4C3B] align-text-bottom" />.</Step>
          <Step n="3">Tap <strong>&ldquo;Add to Home Screen,&rdquo;</strong> then tap <strong>&ldquo;Add.&rdquo;</strong></Step>
        </ol>
        <button data-testid="pwa-ios-done" onClick={close} className="mt-6 w-full rounded-full bg-[#2C4C3B] text-white py-3.5 text-base font-semibold hover:bg-[#1E3629] transition-colors">Got It</button>
      </Overlay>
    );
  }

  // android fallback instructions
  return (
    <Overlay testid="pwa-android" onClose={close}>
      <Header title="Add I Need a Proverb to your phone" onClose={close} />
      <ol className="mt-4 space-y-4">
        <Step n="1">Open the browser menu <MoreVertical className="inline w-5 h-5 text-[#2C4C3B] align-text-bottom" strokeWidth={1.8} />.</Step>
        <Step n="2">Tap <strong>&ldquo;Add to Home screen&rdquo;</strong> or <strong>&ldquo;Install app&rdquo;</strong>.</Step>
        <Step n="3">Confirm.</Step>
      </ol>
      <button data-testid="pwa-android-done" onClick={close} className="mt-6 w-full rounded-full bg-[#2C4C3B] text-white py-3.5 text-base font-semibold hover:bg-[#1E3629] transition-colors">Got it</button>
    </Overlay>
  );
}

function Header({ title, onClose }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <h3 className="font-serif-display text-2xl text-[#0F172A] leading-snug">{title}</h3>
      <button data-testid="pwa-close" onClick={onClose} className="text-[#94A3B8] hover:text-[#0F172A] shrink-0"><X className="w-5 h-5" /></button>
    </div>
  );
}
function Step({ n, children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="shrink-0 w-7 h-7 rounded-full bg-[#EAF1EA] text-[#2C4C3B] font-semibold flex items-center justify-center text-sm">{n}</span>
      <span className="text-base text-[#334155] leading-relaxed pt-0.5">{children}</span>
    </li>
  );
}
